import React, { useState, useEffect } from "react";

const PixelArtCreator = () => {
  const size = 8;
  const pixelSize = 40;

  // RGB State
  const [r, setR] = useState(0);
  const [g, setG] = useState(0);
  const [b, setB] = useState(0);

  // Matrix state
  const [matrix, setMatrix] = useState(Array.from({ length: size }, () => Array(size).fill("white")));

  // Templates
  const templates = {
   
    smiley: [
      [0,0,1,1,1,1,0,0],[0,1,0,0,0,0,1,0],[1,0,1,0,0,1,0,1],[1,0,0,0,0,0,0,1],
      [1,0,1,0,0,1,0,1],[1,0,0,1,1,0,0,1],[0,1,0,0,0,0,1,0],[0,0,1,1,1,1,0,0]
    ],
    letterA: [
      [0,0,1,1,1,1,0,0],[0,1,1,0,0,1,1,0],[1,1,0,0,0,0,1,1],[1,1,1,1,1,1,1,1],
      [1,1,0,0,0,0,1,1],[1,1,0,0,0,0,1,1],[1,1,0,0,0,0,1,1],[0,0,0,0,0,0,0,0]
    ],
    star: [
  [0,0,0,1,1,0,0,0],
  [0,0,1,1,1,1,0,0],
  [0,1,1,1,1,1,1,0],
  [1,1,1,1,1,1,1,1],
  [0,1,1,1,1,1,1,0],
  [0,0,1,1,1,1,0,0],
  [0,0,0,1,1,0,0,0],
  [0,0,0,0,0,0,0,0]
]

  };

  const rgbString = `rgb(${r},${g},${b})`;

  const handleCellClick = (row, col) => {
    setMatrix(prev => {
      const newMatrix = prev.map(r => [...r]);
      newMatrix[row][col] = newMatrix[row][col] === "white" ? rgbString : "white";
      return newMatrix;
    });
  };

  const updateBinaryDisplay = () => {
    return matrix.map(row => row.map(c => (c === "white" ? 0 : 1)).join(" ")).join("\n");
  };

  const handleClear = () => {
    setMatrix(Array.from({ length: size }, () => Array(size).fill("white")));
  };

  const handleLoadTemplate = (templateName) => {
    if (!templates[templateName]) return alert("Choose a template!");
    const pattern = templates[templateName];
    setMatrix(prev => {
      const newMatrix = prev.map((row, rowIndex) =>
        row.map((_, colIndex) =>
          pattern[rowIndex][colIndex] === 1 ? rgbString : "white"
        )
      );
      return newMatrix;
    });
  };

  const handleDownload = () => {
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = size;
    exportCanvas.height = size;
    const ctx = exportCanvas.getContext("2d");

    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        ctx.fillStyle = matrix[row][col];
        ctx.fillRect(col, row, 1, 1);
      }
    }

    const scaledCanvas = document.createElement("canvas");
    scaledCanvas.width = size * pixelSize;
    scaledCanvas.height = size * pixelSize;
    const sctx = scaledCanvas.getContext("2d");
    sctx.imageSmoothingEnabled = false;
    sctx.drawImage(exportCanvas, 0, 0, scaledCanvas.width, scaledCanvas.height);

    const link = document.createElement("a");
    link.download = "pixel-art.png";
    link.href = scaledCanvas.toDataURL("image/png");
    link.click();
  };

  // Styles
  const styles = {
    body: { fontFamily: "'Comic Sans MS', sans-serif", background: "#f0f8ff", color: "#333", margin: 0, padding: "2rem" },
    container: { display: "flex", gap: "2rem", justifyContent: "center", alignItems: "flex-start", flexWrap: "wrap" },
    left: { background: "#fff", padding: "1rem", borderRadius: "12px", boxShadow: "0 4px 10px rgba(0,0,0,0.1)", flex: 1, minWidth: "300px" },
    right: { background: "#fff", padding: "1rem", borderRadius: "12px", boxShadow: "0 4px 10px rgba(0,0,0,0.1)", flex: 1, minWidth: "250px", textAlign: "center" },
    grid: { display: "grid", gridTemplateColumns: `repeat(${size}, ${pixelSize}px)`, gridTemplateRows: `repeat(${size}, ${pixelSize}px)`, gap: "4px", justifyContent: "start", margin: "1rem 0" },
    cell: { width: `${pixelSize}px`, height: `${pixelSize}px`, border: "2px solid #ccc", cursor: "pointer", transition: "background 0.2s, transform 0.1s" },
    sliderGroup: { display: "flex", flexDirection: "column", alignItems: "center" },
    sliders: { display: "flex", flexDirection: "column", gap: "1rem", margin: "1rem 0", alignItems: "center" },
    preview: { width: "120px", height: "120px", border: "3px solid #333", borderRadius: "12px", margin: "1rem auto", background: "rgb(0,0,0)" },
    binary: { fontFamily: "monospace", background: "#fff", borderRadius: "8px", padding: "10px", boxShadow: "0 2px 6px rgba(0,0,0,0.1)", whiteSpace: "pre", marginTop: "1rem", textAlign: "left" },
    button: { margin: "0.5rem 0", padding: "10px 20px", border: "none", borderRadius: "8px", fontSize: "1rem", cursor: "pointer", transition: "0.3s", color: "white" },
    greenButton: { background: "#4caf50" },
    greenButtonHover: { background: "#43a047" },
    orangeSelect: { background: "#ff9800", marginLeft: 0, color: "white" }
  };

  return (
    <div style={styles.body}>
      <h3 style={{ color: "#ff4081", textAlign: "center" }}> Pixel Art Creator</h3>
      <div style={styles.container}>
        {/* LEFT COLUMN */}
        <div style={styles.left}>
          <h4>Templates & Grid</h4>
          <div>
            <label>Choose Template:</label>
            <select style={styles.orangeSelect} onChange={(e) => handleLoadTemplate(e.target.value)}>
              <option value="">--Select--</option>
              <option value="star"> Diamond</option>
              <option value="smiley">😀 Smiley</option>
              <option value="letterA">🔠 Letter A</option>
            </select>
          </div>
          <div style={styles.grid}>
            {matrix.map((row, rowIndex) =>
              row.map((color, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  style={{ ...styles.cell, background: color }}
                  onClick={() => handleCellClick(rowIndex, colIndex)}
                  onMouseEnter={(e) => e.target.style.transform = "scale(1.1)"}
                  onMouseLeave={(e) => e.target.style.transform = "scale(1)"}
                ></div>
              ))
            )}
          </div>
          <button style={{ ...styles.button, ...styles.greenButton }} onClick={handleClear}>Clear Grid</button>
          <button style={{ ...styles.button, ...styles.greenButton }} onClick={handleDownload}>💾 Download as Image</button>
        </div>

        {/* RIGHT COLUMN */}
        <div style={styles.right}>
          <h4>🌈 RGB colors</h4>
          <div style={styles.sliders}>
            <div style={styles.sliderGroup}>
              <label>Red: {r}</label>
              <input type="range" min="0" max="255" value={r} onChange={(e) => setR(+e.target.value)} />
            </div>
            <div style={styles.sliderGroup}>
              <label>Green: {g}</label>
              <input type="range" min="0" max="255" value={g} onChange={(e) => setG(+e.target.value)} />
            </div>
            <div style={styles.sliderGroup}>
              <label>Blue: {b}</label>
              <input type="range" min="0" max="255" value={b} onChange={(e) => setB(+e.target.value)} />
            </div>
          </div>
          <div style={{...styles.preview, background: rgbString}}></div>
          <p>Hex: {`#${[r,g,b].map(x=>x.toString(16).padStart(2,'0')).join('').toUpperCase()}`}</p>

          <h4> Binary Array</h4>
          <div style={styles.binary}>{updateBinaryDisplay()}</div>
        </div>
      </div>
    </div>
  );
};

export default PixelArtCreator;
